/**
 * MUI v5 theme for the arbitrage console — dense terminal / trading-desk.
 *
 * `createAppTheme(mode)` builds a fully selected theme for one mode from ./tokens.js.
 * Light and dark are two selected sets, not one inverted into the other.
 *
 * Nothing here is mounted. `src/theme/ThemeModeProvider.jsx` wires it up; a later step
 * mounts that provider in App.jsx.
 *
 * WHAT THE CURRENT LOOK BECOMES (reinterpreted, not re-encoded):
 *   #696969 header + white text -> a muted chrome tint (surface.header) with ink.secondary
 *                                  11px semibold labels and a 2px border.strong underrule.
 *                                  The heavy grey band was the loudest thing on a page whose
 *                                  loudest thing should be the data.
 *   1px lightgrey grid          -> a true hairline (border.hairline, 1.26:1 light /
 *                                  1.25:1 dark) — present enough to track a row across 15
 *                                  columns, quiet enough to disappear when you read a number.
 *   #d6eaff hover               -> a neutral ghost wash (interaction.hover). Blue is
 *                                  promoted to mean SELECTED, a state the app cannot
 *                                  currently express at all.
 *   elevation={2} + margin 4px  -> a flat panel: hairline ring, 3px radius, and a shadow
 *                                  ladder ~6x flatter than MUI's Material ramp. Panels are
 *                                  separated by edges, not by lift.
 */

import { createTheme } from '@mui/material/styles'

import tokens, { DEFAULT_MODE, MODES } from './tokens'

const { density, font, radius, spacing, status, statusMeta } = tokens

/** px -> rem against a 16px root. */
const rem = (px) => `${px / 16}rem`

/**
 * MUI requires exactly 25 shadow strings. Material's ramp is far too soft-and-lifted for a
 * console, so it is replaced by a 6-step ladder mapped across the 25 slots. Existing code
 * that says `elevation={2}` therefore gets the new flat panel without being edited.
 */
const buildShadows = (t) => {
  const { ambient, key } = t.shadow
  const ladder = [
    'none',
    `0 1px 2px ${ambient}`,
    `0 1px 3px ${ambient}, 0 1px 1px ${key}`,
    `0 2px 6px ${ambient}, 0 1px 2px ${key}`,
    `0 6px 16px ${ambient}, 0 2px 4px ${key}`,
    `0 14px 36px ${ambient}, 0 4px 8px ${key}`,
  ]
  const step = (i) => {
    if (i <= 2) return i
    if (i <= 7) return 3
    if (i <= 15) return 4

    return 5
  }

  return Array.from({ length: 25 }, (unused, i) => ladder[step(i)])
}

/**
 * Type scale. Explicit rem values, so it does not drift when MUI's own defaults change.
 * NOTE: no `fontVariantNumeric` here on purpose — headline and hero figures keep
 * proportional figures. tabular-nums is applied only to MuiTableCell and to the
 * `.tabular-nums` utility (axis ticks), per the reference's figures rule.
 */
const buildTypography = () => ({
  fontFamily: font.sans,
  fontSize: density.fontSize.base,
  htmlFontSize: 16,
  fontWeightLight: font.weight.regular,
  fontWeightRegular: font.weight.regular,
  fontWeightMedium: font.weight.medium,
  fontWeightBold: font.weight.semibold,
  h1: { fontSize: rem(26), lineHeight: 1.2, fontWeight: font.weight.semibold, letterSpacing: '-0.01em' },
  h2: { fontSize: rem(22), lineHeight: 1.22, fontWeight: font.weight.semibold, letterSpacing: '-0.01em' },
  h3: { fontSize: rem(18), lineHeight: 1.25, fontWeight: font.weight.semibold },
  h4: { fontSize: rem(16), lineHeight: 1.3, fontWeight: font.weight.semibold },
  h5: { fontSize: rem(density.fontSize.lead), lineHeight: 1.3, fontWeight: font.weight.semibold },
  h6: { fontSize: rem(density.fontSize.base), lineHeight: 1.35, fontWeight: font.weight.semibold },
  subtitle1: { fontSize: rem(density.fontSize.base), lineHeight: density.lineHeight.base, fontWeight: font.weight.medium },
  subtitle2: { fontSize: rem(density.fontSize.table), lineHeight: density.lineHeight.table, fontWeight: font.weight.semibold },
  body1: { fontSize: rem(density.fontSize.base), lineHeight: density.lineHeight.base },
  body2: { fontSize: rem(density.fontSize.table), lineHeight: density.lineHeight.table },
  button: { fontSize: rem(density.fontSize.table), lineHeight: 1.3, fontWeight: font.weight.semibold, textTransform: 'none', letterSpacing: 0 },
  caption: { fontSize: rem(density.fontSize.caption), lineHeight: 1.35 },
  overline: {
    fontSize: rem(density.fontSize.micro),
    lineHeight: 1.3,
    fontWeight: font.weight.semibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
})

const buildPalette = (mode) => {
  const t = tokens[mode]

  return {
    mode,
    // contrastThreshold 4.5 (MUI default 3) — anything MUI picks text for lands on AA.
    contrastThreshold: 4.5,
    tonalOffset: 0.15,
    common: { black: '#000000', white: '#ffffff' },
    primary: { main: t.accent.main, light: t.accent.light, dark: t.accent.dark, contrastText: t.accent.contrastText },
    secondary: { main: t.ink.secondary, light: t.ink.muted, dark: t.ink.primary, contrastText: t.surface.panel },
    // error / warning / success carry the FIXED status hexes in `main` — those are the
    // reserved MARK colours and are identical in both modes by design.
    //
    // But MUI also uses `main` as a TEXT colour for color="error" / color="success" on
    // Button, Typography, IconButton and FormHelperText. The reserved hexes are explicitly
    // NOT text-safe (tokens.js documents status.good at 3.18:1 and status.critical at
    // 3.60:1 on dark), so `main` alone regressed <Button color="success"> from MUI's
    // default 5.13:1 down to 3.18:1 — below AA, in the default mode.
    //
    // `light` and `dark` are therefore given explicitly, per mode, from the already-measured
    // delta tokens. This also stops MUI's augmentColor deriving them at runtime: its
    // lighten(#d03b3b, 0.15) lands at 4.47:1 on the dark panel, under the 4.5:1 floor.
    // Components wanting text use the text-safe step; marks and fills keep the reserved hex.
    error: {
      main: status.critical,
      light: t.delta.down,
      dark: t.delta.down,
      contrastText: '#ffffff',
    },
    warning: {
      main: status.warning,
      light: t.ink.primary,
      dark: t.ink.primary,
      contrastText: '#0e131a',
    },
    info: { main: t.accent.main, light: t.accent.light, dark: t.accent.dark, contrastText: t.accent.contrastText },
    success: {
      main: status.good,
      light: t.delta.up,
      dark: t.delta.up,
      contrastText: '#0e131a',
    },
    background: { default: t.surface.canvas, paper: t.surface.panel },
    text: { primary: t.ink.primary, secondary: t.ink.secondary, disabled: t.ink.disabled },
    divider: t.border.hairline,
    action: {
      active: t.ink.secondary,
      hover: t.interaction.hover,
      selected: t.interaction.selected,
      // These are OPACITY MULTIPLIERS, not colours. MUI feeds them to alpha() against
      // the *component's own* colour — ToggleButton uses alpha(text.primary, …), Checkbox
      // alpha(primary.main, …), and so on. They are NOT the opacity of the two tokens above.
      //
      // Setting them to 1 to "make the washes opaque" is a category error: it made the
      // selected ToggleButton in TradeDirection.jsx render text.primary ON text.primary —
      // 1.00:1, literally invisible — and made ToggleButton's selected+hover state request
      // opacity 2.0, which MUI clamps while logging "value 2 is out of range [0, 1]".
      //
      // MUI's defaults are correct for the alpha-based components. Where an opaque wash IS
      // wanted, it is set explicitly as a backgroundColor in the component overrides below
      // (MenuItem, TableRow, ToggleButton, Button) — which is what makes those opaque, not this.
      hoverOpacity: 0.04,
      selectedOpacity: 0.08,
      disabled: t.ink.disabled,
      disabledBackground: t.surface.sunken,
      focus: t.interaction.focusRing,
    },
    // ── additive, app-specific roles. Read these, not raw hexes, in components. ──
    surface: t.surface,
    ink: t.ink,
    border: t.border,
    interaction: t.interaction,
    /** FIXED status palette + its mandatory icon/shape/label metadata. */
    status: { ...status, meta: statusMeta },
    /** Signed money. Always shipped with the sign and a direction arrow — never colour alone. */
    delta: t.delta,
    /** Categorical series in FIXED slot order. Do not re-order. */
    series: t.chart.series,
    chart: t.chart,
  }
}

const buildComponents = (mode, t) => ({
  MuiCssBaseline: {
    styleOverrides: {
      // ── the CSS-module bridge ──────────────────────────────────────────────────────
      // 1,613 lines of CSS Modules cannot read a JS theme, which is why they still carry
      // hardcoded hexes and why forcing dark mode produces a near-black page with pure-white
      // table slabs bleeding through. Publishing the tokens as custom properties lets those
      // files participate in theming without being rewritten first: a module swaps
      // `background-color: #696969` for `var(--surface-header)` and becomes mode-aware in
      // one line. Remove a variable only once no module references it.
      ':root': {
        colorScheme: mode,
        '--surface-canvas': t.surface.canvas,
        '--surface-panel': t.surface.panel,
        '--surface-raised': t.surface.raised,
        '--surface-sunken': t.surface.sunken,
        '--surface-header': t.surface.header,
        '--surface-backdrop': t.surface.backdrop,
        '--surface-inverse': t.surface.inverse,
        '--ink-primary': t.ink.primary,
        '--ink-secondary': t.ink.secondary,
        '--ink-muted': t.ink.muted,
        '--ink-disabled': t.ink.disabled,
        '--ink-on-inverse': t.ink.onInverse,
        '--border-hairline': t.border.hairline,
        '--border-default': t.border.default,
        '--border-strong': t.border.strong,
        '--interaction-hover': t.interaction.hover,
        '--interaction-selected': t.interaction.selected,
        '--accent-main': t.accent.main,
        '--delta-up': t.delta.up,
        '--delta-down': t.delta.down,
        '--delta-flat': t.delta.flat,
        '--weight-strong': String(font.weight.semibold),
        // Hashes, addresses, tx ids — and now the Config page's keys and raw values.
        '--font-mono': font.mono,
        // Status marks. Reserved hexes, identical in both modes — see tokens.js. Not
        // text-safe on every surface: pair with an icon and a label, never colour alone.
        '--status-good': status.good,
        '--status-warning': status.warning,
        '--status-serious': status.serious,
        '--status-critical': status.critical,
      },
      body: {
        backgroundColor: t.surface.canvas,
        color: t.ink.primary,
        fontFamily: font.sans,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      code: { fontFamily: font.mono },
      /** Digits that must align vertically — chart axis ticks, ad-hoc numeric columns. */
      '.tabular-nums': { fontVariantNumeric: font.figures.tabular },
      /** Escape hatch for a hero/stat figure that lands inside a table cell. */
      '.figures-proportional': { fontVariantNumeric: font.figures.proportional },
      '.mono': { fontFamily: font.mono },
      '*::-webkit-scrollbar': { width: 10, height: 10 },
      '*::-webkit-scrollbar-track': { backgroundColor: t.surface.sunken },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: t.border.strong,
        borderRadius: radius.pill,
        border: `2px solid ${t.surface.sunken}`,
      },
      '*::-webkit-scrollbar-thumb:hover': { backgroundColor: t.ink.muted },
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: t.surface.panel,
        backgroundImage: 'none', // kills MUI's dark-mode elevation overlay — our dark is selected
        border: `${density.hairline}px solid ${t.border.hairline}`,
        borderRadius: radius.sm,
      },
      outlined: { borderColor: t.border.default },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: { backgroundColor: t.surface.panel },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: {
        backgroundColor: t.surface.panel,
        borderCollapse: 'separate',
        borderSpacing: 0,
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: `${density.cellPaddingY}px ${density.cellPaddingX}px`,
        height: density.rowHeight,
        fontSize: rem(density.fontSize.table),
        lineHeight: density.lineHeight.table,
        // Digits align down the column. This is the one place tabular figures belong.
        fontVariantNumeric: font.figures.tabular,
        color: t.ink.primary,
        borderBottom: `${density.hairline}px solid ${t.border.hairline}`,
        '&:not(:last-of-type)': { borderRight: `${density.hairline}px solid ${t.border.hairline}` },

        // Row height is set by the tallest thing in the row, and in this app that is almost
        // never the text — it is the controls. Measured before this block: a stock Checkbox
        // is 42px and a stock IconButton 26px, so the Bots list ran 69px rows and Bookkeeping
        // 37px against a 28px target. Compacting the text alone would have done nothing.
        //
        // Scoped to table cells on purpose: a 24px checkbox is right in a dense grid and
        // wrong in a form, where the 42px hit target is doing real work.
        '& .MuiCheckbox-root, & .MuiRadio-root': {
          padding: spacing.xxs,
          '& .MuiSvgIcon-root': { fontSize: rem(16) },
        },
        '& .MuiIconButton-root': {
          padding: spacing.xxs,
          '& .MuiSvgIcon-root': { fontSize: rem(16) },
        },
        '& .MuiSwitch-root': { marginTop: -4, marginBottom: -4 },
        // The Bots list Controls column is a 4-button ButtonGroup that wraps to two rows in
        // its column width; at MUI's default 28px per button that alone made the row 53px.
        '& .MuiButtonGroup-root .MuiButton-root, & .MuiButtonGroup-root .MuiIconButton-root': {
          minHeight: 20,
          minWidth: 22,
          padding: `0 ${spacing.xs}px`,
        },
        // Inputs that live in a filter or an editable cell. The Bots list Notes column is
        // a `multiline` outlined TextField at default size — 58px on its own, which set the
        // height of every row in the app's most important table (69px against a 28px target).
        // The outlined variant carries its padding on the ROOT and the textarea inherits
        // none, so both have to be addressed or the field keeps its stock height.
        '& .MuiInputBase-root': { fontSize: rem(density.fontSize.table) },
        '& .MuiOutlinedInput-root': { padding: `${spacing.xxs}px ${spacing.xs}px` },
        '& .MuiOutlinedInput-input, & .MuiInputBase-inputMultiline': {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
      head: {
        height: density.rowHeightHead,
        padding: `${density.cellPaddingYDense}px ${density.cellPaddingX}px`,
        backgroundColor: t.surface.header,
        color: t.ink.secondary,
        fontSize: rem(density.fontSize.caption),
        fontWeight: font.weight.semibold,
        lineHeight: density.lineHeight.base,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        borderBottom: `${density.rule}px solid ${t.border.strong}`,
      },
      stickyHeader: { backgroundColor: t.surface.header },
      footer: {
        backgroundColor: t.surface.sunken,
        color: t.ink.secondary,
        fontWeight: font.weight.semibold,
        borderTop: `${density.rule}px solid ${t.border.strong}`,
      },
      sizeSmall: { padding: `${density.cellPaddingYDense}px ${density.cellPaddingXDense}px`, height: 'auto' },
      paddingCheckbox: { padding: `0 ${density.cellPaddingYDense}px` },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': { backgroundColor: t.interaction.hover },
        '&.Mui-selected': {
          backgroundColor: t.interaction.selected,
          '&:hover': { backgroundColor: t.interaction.selectedHover },
        },
        '&:last-of-type .MuiTableCell-body': { borderBottom: 0 },
      },
      head: { '&:hover': { backgroundColor: 'transparent' } },
      footer: { '&:hover': { backgroundColor: 'transparent' } },
    },
  },

  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        color: 'inherit',
        '&:hover': { color: t.ink.primary },
        '&.Mui-active': { color: t.ink.primary, '& .MuiTableSortLabel-icon': { color: t.accent.main } },
      },
      icon: { fontSize: rem(14) },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: { color: t.ink.secondary, borderTop: `${density.hairline}px solid ${t.border.hairline}` },
      toolbar: { minHeight: density.controlHeight, paddingLeft: spacing.sm },
      selectLabel: { fontSize: rem(density.fontSize.caption) },
      displayedRows: { fontSize: rem(density.fontSize.caption), fontVariantNumeric: font.figures.tabular },
    },
  },

  MuiButton: {
    defaultProps: { size: 'small', disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
        minHeight: density.controlHeightSmall,
        padding: `${spacing.xs}px ${spacing.md}px`,
        gap: spacing.xs,
      },
      sizeSmall: { minHeight: 24, padding: `${spacing.xxs}px ${spacing.sm}px`, fontSize: rem(density.fontSize.caption) },
      sizeLarge: { minHeight: density.controlHeight, padding: `${spacing.sm}px ${spacing.lg}px`, fontSize: rem(density.fontSize.base) },
      outlined: { borderColor: t.border.default, '&:hover': { borderColor: t.border.strong, backgroundColor: t.interaction.hover } },
      text: { '&:hover': { backgroundColor: t.interaction.hover } },
      containedPrimary: { '&:hover': { backgroundColor: t.accent.dark } },

      // Status colours as TEXT. MUI's text/outlined variants paint the label with
      // palette[color].main — and the reserved status hexes are mark colours, explicitly
      // not text-safe (status.good is 3.18:1 on the light panel). Left alone, this repo's
      // six <Button color='error'> sites and ConfirmButton's success/error pair render
      // below AA — and color="success" would be a REGRESSION against MUI's own default,
      // which measured 5.13:1. Redirect the label to the text-safe step; the border and
      // any filled variant keep the reserved hex.
      textError: { color: t.delta.down },
      outlinedError: { color: t.delta.down, borderColor: status.critical },
      textSuccess: { color: t.delta.up },
      outlinedSuccess: { color: t.delta.up, borderColor: status.good },
      textWarning: { color: t.ink.primary },
      outlinedWarning: { color: t.ink.primary, borderColor: status.warning },
    },
  },

  MuiIconButton: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: {
        padding: spacing.xs,
        borderRadius: radius.sm,
        color: t.ink.secondary,
        '&:hover': { backgroundColor: t.interaction.hover, color: t.ink.primary },
      },
      sizeSmall: { padding: spacing.xxs },
    },
  },

  MuiDialog: {
    defaultProps: { PaperProps: { elevation: 16 } },
    styleOverrides: {
      paper: {
        backgroundColor: t.surface.raised,
        border: `${density.hairline}px solid ${t.border.default}`,
        borderRadius: radius.md,
        backgroundImage: 'none',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: rem(density.fontSize.lead),
        fontWeight: font.weight.semibold,
        padding: `${spacing.md}px ${spacing.lg}px`,
        borderBottom: `${density.hairline}px solid ${t.border.hairline}`,
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { padding: `${spacing.lg}px`, fontSize: rem(density.fontSize.base) },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: `${spacing.sm}px ${spacing.lg}px`,
        gap: spacing.sm,
        borderTop: `${density.hairline}px solid ${t.border.hairline}`,
      },
    },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: { backgroundColor: t.surface.backdrop },
      invisible: { backgroundColor: 'transparent' },
    },
  },

  MuiTextField: {
    defaultProps: { size: 'small', variant: 'outlined' },
  },

  MuiInputBase: {
    styleOverrides: {
      root: { fontSize: rem(density.fontSize.base), color: t.ink.primary },
      input: {
        // ink.muted is 3.45:1 on the light panel — documented in tokens.js as axis-tick
        // and decorative use. A placeholder names the field, so it is read: text-safe step.
        '&::placeholder': { color: t.ink.secondary, opacity: 1 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: t.surface.panel,
        borderRadius: radius.sm,
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.border.strong },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: density.hairline, borderColor: t.border.focus },
        '&.Mui-focused': { boxShadow: `0 0 0 2px ${t.interaction.focusRing}` },
        '&.Mui-disabled': { backgroundColor: t.surface.sunken },
      },
      notchedOutline: { borderColor: t.border.default },
      input: { padding: `${density.cellPaddingY}px ${spacing.sm}px`, height: 'auto' },
      inputSizeSmall: { padding: `${density.cellPaddingYDense}px ${spacing.sm}px` },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      // Same reasoning as the placeholder above: a resting float label IS the field's
      // name, so it has to clear AA rather than sit at the decorative 3.45:1.
      root: { fontSize: rem(density.fontSize.table), color: t.ink.secondary, '&.Mui-focused': { color: t.border.focus } },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: { fontSize: rem(density.fontSize.caption), marginLeft: 0, marginTop: spacing.xs, color: t.ink.muted },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 32,
        borderBottom: `${density.hairline}px solid ${t.border.hairline}`,
      },
      indicator: { height: density.rule, backgroundColor: t.accent.main },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 32,
        padding: `${spacing.xs}px ${spacing.md}px`,
        fontSize: rem(density.fontSize.table),
        fontWeight: font.weight.semibold,
        textTransform: 'none',
        letterSpacing: 0,
        // MUI's default 90px min-width (and its >=600px media-query variant, which is what
        // actually applies on a desktop console) sized these tabs for touch. With 15 tabs at
        // 12px the strip measured 1438px inside a 1284px scroller at a 1600px viewport — so
        // the PRIMARY NAV overflowed and grew scroll chevrons on a wide monitor, with
        // "Exchanges" clipped to "Ex". "Bots" needs ~52px and was given 90. Both declarations
        // have to go: overriding only the base leaves the media query winning.
        minWidth: 0,
        '@media (min-width:600px)': { minWidth: 0 },
        color: t.ink.muted,
        '&:hover': { color: t.ink.primary, backgroundColor: t.interaction.hover },
        '&.Mui-selected': { color: t.accent.main },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: t.surface.raised,
        border: `${density.hairline}px solid ${t.border.default}`,
        borderRadius: radius.sm,
        backgroundImage: 'none',
      },
      list: { paddingTop: spacing.xs, paddingBottom: spacing.xs },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        minHeight: density.controlHeightSmall,
        fontSize: rem(density.fontSize.table),
        padding: `${spacing.xs}px ${spacing.md}px`,
        '&:hover': { backgroundColor: t.interaction.hover },
        '&.Mui-selected': {
          backgroundColor: t.interaction.selected,
          '&:hover': { backgroundColor: t.interaction.selectedHover },
        },
      },
    },
  },

  // Un-overridden, MUI paints the selected state as alpha(text.primary, 0.08) — a ~8% grey
  // wash. That is too weak for a trade-direction selector, where "which side am I on" must be
  // readable at a glance across a room. Use the same designed tokens as MenuItem and TableRow,
  // and carry selection on the border and weight too, so it is never colour-alone.
  MuiToggleButton: {
    styleOverrides: {
      root: {
        minHeight: density.controlHeightSmall,
        padding: `${spacing.xs}px ${spacing.md}px`,
        fontSize: rem(density.fontSize.base),
        fontWeight: font.weight.medium,
        letterSpacing: 0,
        textTransform: 'none',
        color: t.ink.secondary,
        borderColor: t.border.default,
        '&:hover': { backgroundColor: t.interaction.hover, color: t.ink.primary },
        '&.Mui-selected': {
          color: t.ink.primary,
          fontWeight: font.weight.semibold,
          backgroundColor: t.interaction.selected,
          borderColor: t.border.strong,
          '&:hover': { backgroundColor: t.interaction.selectedHover },
        },
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: t.surface.raised,
        border: `${density.hairline}px solid ${t.border.default}`,
        borderRadius: radius.sm,
        backgroundImage: 'none',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: t.surface.inverse,
        color: t.ink.onInverse,
        fontSize: rem(density.fontSize.caption),
        borderRadius: radius.xs,
        padding: `${spacing.xs}px ${spacing.sm}px`,
      },
      arrow: { color: t.surface.inverse },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderColor: t.border.hairline },
    },
  },

  MuiChip: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: { borderRadius: radius.xs, fontSize: rem(density.fontSize.caption), fontWeight: font.weight.medium },
      sizeSmall: { height: 20 },
      outlined: { borderColor: t.border.default },
    },
  },

  MuiLink: {
    defaultProps: { underline: 'hover' },
    styleOverrides: {
      root: { color: t.accent.main, fontWeight: font.weight.medium },
    },
  },
})

/**
 * Build the MUI theme for one mode. Unknown modes fall back to the default (light).
 */
export const createAppTheme = (mode = DEFAULT_MODE) => {
  const resolved = MODES.includes(mode) ? mode : DEFAULT_MODE
  const t = tokens[resolved]

  return createTheme({
    palette: buildPalette(resolved),
    typography: buildTypography(),
    // 4px base: spacing(1)=4, (2)=8, (3)=12, (4)=16, (5)=20, (6)=24.
    spacing: spacing.base,
    shape: { borderRadius: radius.sm },
    shadows: buildShadows(t),
    components: buildComponents(resolved, t),
  })
}

/** Both modes, pre-built. Use these unless you need a fresh instance. */
export const lightTheme = createAppTheme('light')
export const darkTheme = createAppTheme('dark')

export const themes = { light: lightTheme, dark: darkTheme }

export { tokens, MODES, DEFAULT_MODE }

export default createAppTheme
